/**
 * Sidebar.jsx — Left Sidebar Panel
 *
 * Contains:
 *  - New chat button
 *  - Recent conversation list (mock history)
 *  - SAP Module quick-access chips
 *  - User info + logout at the bottom
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, MessageSquare, Package, ShoppingCart, DollarSign,
  Factory, Warehouse, Headphones, LogOut, ChevronRight, ChevronLeft,
  X, Menu, Users, UserCircle, Trash2, BarChart2, TicketCheck
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { listConversations, deleteConversation } from '../utils/api';
import logger from '../utils/logger';
import BrandLogo from './BrandLogo';
import './Sidebar.css';

// ── SAP Module definitions ────────────────────────────────────────────────────
const SAP_MODULES = [
  { id: 'MM',   label: 'Materials Mgmt',     icon: Package,      color: 'var(--clr-mm)',   sample: 'Show open purchase orders' },
  { id: 'SD',   label: 'Sales & Distribution', icon: ShoppingCart, color: 'var(--clr-sd)',   sample: 'Display sales orders for today' },
  { id: 'FI',   label: 'Finance',             icon: DollarSign,   color: 'var(--clr-fi)',   sample: 'Show overdue vendor invoices' },
  { id: 'PP',   label: 'Production Planning', icon: Factory,      color: 'var(--clr-pp)',   sample: 'List open production orders' },
  { id: 'EWM',  label: 'Warehouse Mgmt',      icon: Warehouse,    color: '#06B6D4',         sample: 'Show warehouse transfer orders' },
  { id: 'ITSM', label: 'IT Service Mgmt',     icon: Headphones,   color: 'var(--clr-itsm)', sample: 'Show open P1 incidents' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Returns a relative label for a conversation's updated_at timestamp. */
function relativeLabel(dateStr) {
  const d     = new Date(dateStr);
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const conv  = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((today - conv) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7)   return d.toLocaleDateString('en-US', { weekday: 'short' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * @param {function} onNewChat           - Called when user starts a new conversation
 * @param {function} onModuleClick       - Called with a sample query string when module is clicked
 * @param {string}   activeChat          - ID of the currently active conversation
 * @param {function} onLoadConversation  - Called with conversationId when user clicks a past chat
 * @param {number}   refreshKey          - Increment from parent to trigger conversation list refresh
 */
export default function Sidebar({ onNewChat, onModuleClick, activeChat, onLoadConversation, refreshKey }) {
  const { user, logout, brand, token } = useAuth();
  const navigate                       = useNavigate();
  const [mobileOpen,        setMobileOpen]        = useState(false);
  const [collapsed,         setCollapsed]         = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const [conversations,     setConversations]     = useState([]);
  const [deleteConfirm,     setDeleteConfirm]     = useState(null); // conv object pending delete
  const [deletingConv,      setDeletingConv]      = useState(false);

  // ── Persist collapse preference ────────────────────────────────────────────
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(collapsed));
  }, [collapsed]);

  // ── Fetch conversation list ────────────────────────────────────────────────
  const loadConversations = useCallback(() => {
    if (!token) return;
    listConversations(token)
      .then(setConversations)
      .catch((err) => logger.warn('Sidebar: Failed to load conversations', err));
  }, [token]);

  useEffect(() => { loadConversations(); }, [loadConversations, refreshKey]);

  // ── Delete a conversation (ask first) ────────────────────────────────────────
  const requestDelete = useCallback((e, conv) => {
    e.stopPropagation();
    setDeleteConfirm(conv);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm) return;
    setDeletingConv(true);
    try {
      await deleteConversation(token, deleteConfirm.conversation_id);
      setConversations((prev) => prev.filter((c) => c.conversation_id !== deleteConfirm.conversation_id));
      setDeleteConfirm(null);
    } catch (err) {
      logger.error('Sidebar: Failed to delete conversation', err);
    } finally {
      setDeletingConv(false);
    }
  }, [token, deleteConfirm]);

  const brandName  = brand?.brand_name  || 'Querely';
  const brandColor = brand?.brand_color || null;

  const handleLogout = async () => {
    logger.info('Sidebar: Logout clicked');
    await logout();
  };

  const handleModuleClick = (module) => {
    logger.debug('Sidebar: Module chip clicked', { module: module.id });
    onModuleClick(module.sample);
    setMobileOpen(false); // Close sidebar on mobile after selection
  };

  const handleNewChat = () => {
    onNewChat();
    setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile toggle button */}
      <button
        className="sidebar-mobile-toggle"
        onClick={() => setMobileOpen((v) => !v)}
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileOpen}
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Overlay for mobile */}
      {mobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Desktop collapse / expand toggle */}
      <button
        className={`sidebar-collapse-toggle ${collapsed ? 'sidebar-collapse-toggle--collapsed' : ''}`}
        onClick={() => setCollapsed((v) => !v)}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      {/* Sidebar panel */}
      <aside className={`sidebar ${mobileOpen ? 'sidebar--open' : ''} ${collapsed ? 'sidebar--collapsed' : ''}`} aria-label="Navigation sidebar">

        {/* ── Brand ── */}
        <div className="sidebar-brand">
          <div className="sidebar-logo-badge">
            <BrandLogo size={42} />
          </div>
          <div className="sidebar-brand-info">
            <div className="sidebar-brand-name-row">
              <span className="sidebar-logo">Querely</span>
              <sup className="tm sidebar-tm">™</sup>
            </div>
            <span className="sidebar-brand-sub">{brandName}</span>
          </div>
        </div>

        {/* ── New Chat ── */}
        <button className="sidebar-new-chat" onClick={handleNewChat}>
          <Plus size={16} />
          New conversation
        </button>

        {/* ── SAP Modules (filtered to what tenant has access to) ── */}
        <section className="sidebar-section">
          <h2 className="sidebar-section-title">SAP Modules</h2>
          <div className="sidebar-modules">
            {SAP_MODULES.filter(mod =>
              !user?.modules?.length || user.modules.includes(mod.id)
            ).map((mod) => {
              const Icon = mod.icon;
              return (
                <button
                  key={mod.id}
                  className="sidebar-module-chip"
                  onClick={() => handleModuleClick(mod)}
                  style={{ '--mod-color': mod.color }}
                  title={`Ask about ${mod.label}`}
                >
                  <Icon size={13} />
                  <span>{mod.id}</span>
                  <ChevronRight size={11} className="sidebar-module-arrow" />
                </button>
              );
            })}
          </div>
        </section>

        {/* ── Recent Chats ── */}
        <section className="sidebar-section sidebar-section--grow">
          <h2 className="sidebar-section-title">Recent</h2>
          <nav className="sidebar-recent" aria-label="Recent conversations">
            {conversations.length === 0 && (
              <p className="sidebar-chat-empty">No conversations yet</p>
            )}
            {conversations.map((conv) => (
              <button
                key={conv.conversation_id}
                className={`sidebar-chat-item ${activeChat === conv.conversation_id ? 'sidebar-chat-item--active' : ''}`}
                aria-current={activeChat === conv.conversation_id ? 'page' : undefined}
                onClick={() => { onLoadConversation?.(conv.conversation_id); setMobileOpen(false); }}
                title={conv.title}
              >
                <MessageSquare size={13} className="sidebar-chat-icon" />
                <span className="sidebar-chat-title">{conv.title}</span>
                <span className="sidebar-chat-time">{relativeLabel(conv.updated_at)}</span>
                <button
                  className="sidebar-chat-delete"
                  onClick={(e) => requestDelete(e, conv)}
                  title="Delete conversation"
                  aria-label="Delete conversation"
                >
                  <Trash2 size={11} />
                </button>
              </button>
            ))}
          </nav>
        </section>

        {/* ── User Footer ── */}
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-avatar" aria-hidden="true">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.name || 'User'}</div>
              <div className="sidebar-user-role">{user?.role || 'SAP User'} · {user?.tenant}</div>
            </div>
          </div>

          <div className="sidebar-user-modules">
            {user?.modules?.map((m) => (
              <span key={m} className="sidebar-user-module-badge">{m}</span>
            ))}
          </div>

          <div className="sidebar-user-actions">
            {user?.role === 'admin' && (
              <button className="sidebar-action-btn" onClick={() => { navigate('/manage-users'); setMobileOpen(false); }}>
                <Users size={13} />
                Manage Users
              </button>
            )}
            {user?.role === 'admin' && (
              <button className="sidebar-action-btn" onClick={() => { navigate('/usage'); setMobileOpen(false); }}>
                <BarChart2 size={13} />
                Usage
              </button>
            )}
            <button className="sidebar-action-btn" onClick={() => { navigate('/support'); setMobileOpen(false); }}>
              <TicketCheck size={13} />
              Support
            </button>
            <button className="sidebar-action-btn" onClick={() => { navigate('/profile'); setMobileOpen(false); }}>
              <UserCircle size={13} />
              My Profile
            </button>
            <button className="sidebar-logout" onClick={handleLogout}>
              <LogOut size={14} />
              Sign out
            </button>
          </div>

          <div className="sidebar-legal-links">
            <button className="sidebar-legal-link" onClick={() => { navigate('/privacy'); setMobileOpen(false); }}>
              Privacy Policy
            </button>
            <span className="sidebar-legal-sep" aria-hidden="true">·</span>
            <button className="sidebar-legal-link" onClick={() => { navigate('/terms'); setMobileOpen(false); }}>
              Terms of Service
            </button>
          </div>
        </div>
      </aside>

      {/* ── Conversation delete confirmation ── */}
      {deleteConfirm && (
        <div className="sidebar-confirm-backdrop" onClick={() => setDeleteConfirm(null)}>
          <div className="sidebar-confirm-dialog" onClick={e => e.stopPropagation()}>
            <p className="sidebar-confirm-text">
              Delete "<strong>{deleteConfirm.title}</strong>"?<br />
              <span style={{ fontSize: 12, opacity: 0.7 }}>This cannot be undone.</span>
            </p>
            <div className="sidebar-confirm-actions">
              <button className="sidebar-confirm-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="sidebar-confirm-delete" onClick={confirmDelete} disabled={deletingConv}>
                {deletingConv ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
